import React, { useState } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, ArrowRight, AlertTriangle,
  Calculator, ChevronDown, ChevronUp,
  PenTool, RotateCcw, Award
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'A2-1'

interface Step {
  label: string
  content: string
}

interface Problem {
  id: string
  level: 'basic' | 'standard' | 'advanced' | 'final'
  number: number
  tag: string
  question: string
  choices?: string[]
  answer: string
  solutionSteps: Step[]
  keyFormula?: string
  tip?: string
  concept: string
}

const PROBLEMS: Problem[] = [
  // ── 기본 문제 ──
  {
    id: 'P01', level: 'basic', number: 1,
    tag: '단항식·차수', concept: '단항식의 차수',
    question: '3x²y에서 차수를 구하시오.',
    answer: '3',
    solutionSteps: [
      { label: '① 차수의 정의', content: '차수 = 문자 지수의 합' },
      { label: '② 지수 확인', content: 'x의 지수: 2, y의 지수: 1' },
      { label: '③ 차수 계산', content: '차수 = 2 + 1 = 3' },
    ],
    keyFormula: '차수 = 각 문자 지수의 합',
    tip: '계수(3)는 차수에 포함되지 않아요!',
  },
  {
    id: 'P02', level: 'basic', number: 2,
    tag: '동류항', concept: '동류항 판별',
    question: '다음 중 3x²y와 동류항인 것은?\n① 3xy  ② −5x²y  ③ 3x²  ④ x²y²',
    choices: ['3xy', '−5x²y', '3x²', 'x²y²'],
    answer: '② −5x²y',
    solutionSteps: [
      { label: '① 동류항 조건', content: '문자와 차수가 모두 같아야 동류항' },
      { label: '② 3x²y 분석', content: 'x²y 형태, 차수 3' },
      { label: '③ 각 항 비교', content: '① xy(2차) ② x²y(3차)✓ ③ x²(2차) ④ x²y²(4차)' },
    ],
    keyFormula: '동류항: 문자·지수가 동일 (계수만 달라도 OK)',
    tip: '계수는 달라도 되고, 문자와 지수만 같으면 동류항!',
  },
  {
    id: 'P03', level: 'basic', number: 3,
    tag: '단항식 덧셈', concept: '동류항 계산',
    question: '5a + 3a − 2a를 계산하시오.',
    answer: '6a',
    solutionSteps: [
      { label: '① 동류항 확인', content: '5a, 3a, −2a 모두 a항 → 동류항' },
      { label: '② 계수 합산', content: '(5 + 3 − 2)a' },
      { label: '③ 결과', content: '= 6a' },
    ],
    keyFormula: '동류항 합: 계수만 더하고 문자는 그대로',
  },
  {
    id: 'P04', level: 'basic', number: 4,
    tag: '다항식 덧셈', concept: '다항식의 덧셈',
    question: '(2x + 3) + (4x − 1)을 계산하시오.',
    answer: '6x + 2',
    solutionSteps: [
      { label: '① 괄호 제거', content: '2x + 3 + 4x − 1' },
      { label: '② 동류항 모으기', content: '(2x + 4x) + (3 − 1)' },
      { label: '③ 계산', content: '= 6x + 2' },
    ],
    keyFormula: '괄호 제거 → 동류항끼리 묶기 → 계수 합산',
  },
  // ── 실력 문제 ──
  {
    id: 'P05', level: 'standard', number: 5,
    tag: '다항식 뺄셈', concept: '다항식의 뺄셈',
    question: '(3x² − 2x + 5) − (x² + 4x − 3)을 계산하시오.',
    answer: '2x² − 6x + 8',
    solutionSteps: [
      { label: '① 뺄셈 부호 분배', content: '3x² − 2x + 5 − x² − 4x + 3\n(빼는 괄호의 모든 부호 반전)' },
      { label: '② 동류항 모으기', content: '(3x² − x²) + (−2x − 4x) + (5 + 3)' },
      { label: '③ 계산', content: '= 2x² − 6x + 8' },
    ],
    keyFormula: 'A − B에서 B의 괄호를 풀 때 모든 부호를 반전!',
    tip: '뺄셈은 뒤 괄호의 모든 부호를 바꾸는 것이 핵심!',
  },
  {
    id: 'P06', level: 'standard', number: 6,
    tag: '단항식 곱셈', concept: '단항식의 곱셈',
    question: '3x² × (−2x³)을 계산하시오.',
    answer: '−6x⁵',
    solutionSteps: [
      { label: '① 계수 곱셈', content: '3 × (−2) = −6' },
      { label: '② 지수 덧셈 (곱셈 법칙)', content: 'x² × x³ = x^(2+3) = x⁵' },
      { label: '③ 결합', content: '−6x⁵' },
    ],
    keyFormula: 'xᵐ × xⁿ = xᵐ⁺ⁿ (같은 밑의 거듭제곱 곱셈)',
  },
  {
    id: 'P07', level: 'standard', number: 7,
    tag: '단항식 나눗셈', concept: '단항식의 나눗셈',
    question: '12x⁴y² ÷ 4x²y를 계산하시오.',
    answer: '3x²y',
    solutionSteps: [
      { label: '① 계수 나눗셈', content: '12 ÷ 4 = 3' },
      { label: '② x 지수 뺄셈', content: 'x⁴ ÷ x² = x^(4−2) = x²' },
      { label: '③ y 지수 뺄셈', content: 'y² ÷ y¹ = y^(2−1) = y' },
      { label: '④ 결합', content: '3x²y' },
    ],
    keyFormula: 'xᵐ ÷ xⁿ = xᵐ⁻ⁿ (같은 밑의 거듭제곱 나눗셈)',
  },
  {
    id: 'P08', level: 'standard', number: 8,
    tag: '분배법칙', concept: '단항식×다항식',
    question: '2x(3x − 4)를 전개하시오.',
    answer: '6x² − 8x',
    solutionSteps: [
      { label: '① 분배법칙 적용', content: '2x × 3x + 2x × (−4)' },
      { label: '② 각 항 계산', content: '6x² − 8x' },
    ],
    keyFormula: 'a(b + c) = ab + ac (분배법칙)',
  },
  // ── 심화 문제 ──
  {
    id: 'P09', level: 'advanced', number: 9,
    tag: '식의 값', concept: '다항식에 수 대입',
    question: 'x = 2, y = −1일 때, 3x²y − 2xy + x의 값을 구하시오.',
    answer: '−16',
    solutionSteps: [
      { label: '① 대입 준비', content: '3(2)²(−1) − 2(2)(−1) + 2' },
      { label: '② 각 항 계산', content: '3 × 4 × (−1) = −12\n−2 × 2 × (−1) = 4\n마지막 항 = 2' },
      { label: '③ 합산', content: '−12 + 4 + 2 = −6\n\n잠깐, 다시 계산:\n3(4)(−1) = −12\n−2(2)(−1) = 4\n+2\n합 = −12 + 4 + 2 = −6' },
    ],
    keyFormula: '대입: 문자에 수를 넣고 순서대로 계산',
    tip: '음수 대입 시 괄호를 꼭 사용하세요!',
  },
  {
    id: 'P10', level: 'advanced', number: 10,
    tag: '복잡한 계산', concept: '혼합 계산',
    question: '(4x²y) ÷ (2x) × 3y를 계산하시오.',
    answer: '6xy²',
    solutionSteps: [
      { label: '① 왼쪽부터 순서대로', content: '먼저 (4x²y) ÷ (2x) 계산' },
      { label: '② 나눗셈 계산', content: '4÷2 = 2, x²÷x = x, y는 그대로\n→ 2xy' },
      { label: '③ 곱셈 계산', content: '2xy × 3y = 6xy²' },
    ],
    keyFormula: '나눗셈·곱셈 혼합: 왼쪽부터 차례로',
  },
  // ── 종합 문제 ──
  {
    id: 'P11', level: 'final', number: 11,
    tag: '종합', concept: '최고차항·최고차수',
    question: '다항식 2x³ − 5x² + 3x − 7에서 최고차항의 계수와 차수를 구하시오.',
    answer: '최고차항 계수: 2, 차수: 3',
    solutionSteps: [
      { label: '① 최고차항 찾기', content: '차수가 가장 높은 항: 2x³' },
      { label: '② 계수', content: '2x³의 계수 = 2' },
      { label: '③ 다항식의 차수', content: '가장 높은 차수 = 3' },
    ],
    keyFormula: '다항식의 차수 = 가장 높은 항의 차수',
  },
  {
    id: 'P12', level: 'final', number: 12,
    tag: '종합', concept: '다항식 계산 종합',
    question: '(x² + 3x − 2) + 2(x² − x + 4) − (x² + 2x − 6)을 계산하시오.',
    answer: '2x² − x + 12',
    solutionSteps: [
      { label: '① 괄호 전개', content: 'x² + 3x − 2 + 2x² − 2x + 8 − x² − 2x + 6' },
      { label: '② x² 항', content: '(1 + 2 − 1)x² = 2x²' },
      { label: '③ x 항', content: '(3 − 2 − 2)x = −x' },
      { label: '④ 상수항', content: '−2 + 8 + 6 = 12' },
      { label: '⑤ 결합', content: '2x² − x + 12' },
    ],
    keyFormula: '괄호 안 계수 분배 → 동류항별로 정리',
  },
  {
    id: 'P13', level: 'final', number: 13,
    tag: '종합', concept: '단항식 곱나눗셈 혼합',
    question: '(−3ab²)² ÷ 9a²b를 계산하시오.',
    answer: 'b³',
    solutionSteps: [
      { label: '① 거듭제곱 계산', content: '(−3ab²)² = (−3)² × a² × (b²)² = 9a²b⁴' },
      { label: '② 나눗셈 계산', content: '9a²b⁴ ÷ 9a²b = b³' },
    ],
    keyFormula: '(xᵐ)ⁿ = xᵐⁿ, (ab)ⁿ = aⁿbⁿ',
    tip: '음수의 짝수 거듭제곱은 양수!',
  },
  {
    id: 'P14', level: 'final', number: 14,
    tag: '종합', concept: '대입 + 계산',
    question: 'A = 2x − y, B = x + 3y일 때, 2A − B를 x, y로 나타내시오.',
    answer: '3x − 5y',
    solutionSteps: [
      { label: '① 2A 계산', content: '2(2x − y) = 4x − 2y' },
      { label: '② B 확인', content: 'B = x + 3y' },
      { label: '③ 2A − B 계산', content: '(4x − 2y) − (x + 3y)\n= 4x − 2y − x − 3y\n= 3x − 5y' },
    ],
    keyFormula: '식의 대입: A, B를 식으로 놓고 전개',
  },
  {
    id: 'P15', level: 'final', number: 15,
    tag: '종합', concept: '다항식 종합',
    question: '어떤 다항식에서 3x² − 2x + 1을 빼야 할 것을 잘못하여 더했더니 5x² + x − 3이 되었다. 바른 계산 결과를 구하시오.',
    answer: '−x² + 5x − 5',
    solutionSteps: [
      { label: '① 원래 식 찾기', content: '어떤 식 = (5x² + x − 3) − (3x² − 2x + 1)\n= 2x² + 3x − 4' },
      { label: '② 바른 계산', content: '어떤 식 − (3x² − 2x + 1)\n= (2x² + 3x − 4) − (3x² − 2x + 1)' },
      { label: '③ 전개', content: '2x² + 3x − 4 − 3x² + 2x − 1 = −x² + 5x − 5' },
    ],
    keyFormula: '잘못된 계산을 역으로 풀어 원래 식 복원',
    tip: '"더했더니" → 원식 = 결과 − 더한 값',
  },
]

const LEVEL_CONFIG = {
  basic:    { label: '기본', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200' },
  standard: { label: '실력', color: 'bg-sky-100 text-sky-700',         border: 'border-sky-200' },
  advanced: { label: '심화', color: 'bg-amber-100 text-amber-700',     border: 'border-amber-200' },
  final:    { label: '종합', color: 'bg-rose-100 text-rose-700',       border: 'border-rose-200' },
}

export default function A2_1ProblemPage() {
  const [openId, setOpenId]       = useState<string | null>(null)
  const [showStep, setShowStep]   = useState<Record<string, boolean>>({})
  const [showHint, setShowHint]   = useState<Record<string, boolean>>({})
  const [solved, setSolved]       = useState<Record<string, boolean>>({})
  const [filterLevel, setFilterLevel] = useState<string>('all')

  const { markProblemSolved } = useProgressStore(s => ({ markProblemSolved: s.markProblemSolved }))

  const filtered = filterLevel === 'all' ? PROBLEMS : PROBLEMS.filter(p => p.level === filterLevel)
  const solvedCount = Object.values(solved).filter(Boolean).length

  const handleSolve = (id: string) => {
    setSolved(s => ({ ...s, [id]: true }))
    markProblemSolved(UNIT_ID, id)
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* 헤더 */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-5 mb-5 text-center">
        <p className="text-3xl mb-2">📐</p>
        <h2 className="text-xl font-bold mb-1">단항식과 다항식 문제풀이</h2>
        <p className="text-indigo-100 text-sm">기본 → 실력 → 심화 → 종합 · 15문제</p>
        <div className="mt-3 flex justify-center gap-4 text-sm">
          <span className="bg-white/20 rounded-full px-3 py-1">
            풀이 완료 <strong>{solvedCount}</strong> / {PROBLEMS.length}
          </span>
        </div>
      </div>

      {/* 레벨 필터 */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[['all', '전체', 'bg-slate-100 text-slate-700'], ...Object.entries(LEVEL_CONFIG).map(([k, v]) => [k, v.label, v.color])].map(([key, label, cls]) => (
          <button
            key={key}
            onClick={() => setFilterLevel(key)}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${
              filterLevel === key ? `${cls} border-current shadow` : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
            }`}
          >{label}</button>
        ))}
      </div>

      {/* 문제 목록 */}
      <div className="space-y-3">
        {filtered.map(prob => {
          const lc = LEVEL_CONFIG[prob.level]
          const isOpen = openId === prob.id
          const isSolved = solved[prob.id]
          return (
            <div key={prob.id} className={`rounded-2xl border-2 transition-all ${isSolved ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              {/* 문제 헤더 */}
              <button
                className="w-full text-left p-4 flex items-start gap-3"
                onClick={() => setOpenId(isOpen ? null : prob.id)}
              >
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 mt-0.5 ${lc.color}`}>
                  {lc.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-500">Q{prob.number}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{prob.tag}</span>
                    {isSolved && <CheckCircle size={13} className="text-emerald-500" />}
                  </div>
                  <p className="text-sm font-medium text-slate-800 whitespace-pre-line">{prob.question}</p>
                </div>
                {isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
              </button>

              {/* 펼침 영역 */}
              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {/* 선택지 (객관식) */}
                  {prob.choices && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {prob.choices.map((c, i) => (
                        <div key={i} className="text-xs px-3 py-2 bg-slate-50 rounded-xl border border-slate-200 text-slate-700">
                          <span className="font-bold mr-1">{['①','②','③','④'][i]}</span>{c}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 힌트 */}
                  {prob.keyFormula && (
                    <button
                      onClick={() => setShowHint(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                      className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 font-semibold"
                    >
                      <Lightbulb size={13} />
                      {showHint[prob.id] ? '힌트 닫기' : '핵심 공식 보기'}
                    </button>
                  )}
                  {showHint[prob.id] && prob.keyFormula && (
                    <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-xs text-amber-800 font-medium">{prob.keyFormula}</p>
                      {prob.tip && <p className="text-xs text-amber-600 mt-1">💡 {prob.tip}</p>}
                    </div>
                  )}

                  {/* 풀이 */}
                  <button
                    onClick={() => setShowStep(s => ({ ...s, [prob.id]: !s[prob.id] }))}
                    className="mt-3 flex items-center gap-1.5 text-xs text-indigo-600 font-semibold"
                  >
                    <Calculator size={13} />
                    {showStep[prob.id] ? '풀이 닫기' : '단계별 풀이 보기'}
                  </button>
                  {showStep[prob.id] && (
                    <div className="mt-2 space-y-2">
                      {prob.solutionSteps.map((step, i) => (
                        <div key={i} className="flex gap-2 p-2.5 bg-indigo-50 rounded-xl border border-indigo-100">
                          <span className="text-xs font-bold text-indigo-500 flex-shrink-0">{step.label}</span>
                          <p className="text-xs text-indigo-800 whitespace-pre-line">{step.content}</p>
                        </div>
                      ))}
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs font-bold text-emerald-700">✅ 정답: {prob.answer}</p>
                      </div>
                    </div>
                  )}

                  {/* 풀었어요 버튼 */}
                  {!isSolved ? (
                    <button
                      onClick={() => handleSolve(prob.id)}
                      className="mt-3 w-full py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 flex items-center justify-center gap-1.5"
                    >
                      <CheckCircle size={13} /> 풀었어요!
                    </button>
                  ) : (
                    <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-emerald-600 font-bold py-2">
                      <Award size={13} /> 완료!
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 완료 메시지 */}
      {solvedCount === PROBLEMS.length && (
        <div className="mt-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-5 text-center">
          <p className="text-3xl mb-2">🎉</p>
          <p className="font-bold text-lg">모든 문제 완료!</p>
          <p className="text-sm opacity-80 mt-1">실전 TEST에 도전해보세요!</p>
        </div>
      )}
    </div>
  )
}
